import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Star, Zap, Car } from 'lucide-react';
import { Container } from './Container';
import { Button } from './Button';
import { ConsultationModal } from './ConsultationModal';
import { useScrollSpy } from '../hooks';
import { scrollToElement } from '../utils';
import type { NavItem } from '../types';

interface NavbarProps {
  logo?: string;
  navItems: NavItem[];
  className?: string;
  lightText?: boolean; // 新增：是否使用浅色文字（用于深色背景）
}

/**
 * 导航栏组件
 * 支持响应式设计、滚动监听、下拉菜单等功能
 */
export const Navbar: React.FC<NavbarProps> = ({
  logo = '车为家',
  navItems,
  className = '',
  lightText = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  
  // 获取所有导航项的ID用于滚动监听
  const navIds = navItems.map(item => item.href.replace('#', ''));
  const activeId = useScrollSpy(navIds, 100);
  
  // 监听滚动事件，改变导航栏样式
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // 处理导航点击
  const handleNavClick = (href: string) => {
    if (href.startsWith('#')) {
      const elementId = href.replace('#', '');
      scrollToElement(elementId);
    } else if (href.startsWith('http')) {
      // 外部链接在新窗口打开
      window.open(href, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = href;
    }
    setIsOpen(false);
    setOpenDropdown(null);
  };
  
  // 处理下拉菜单
  const handleDropdownToggle = (label: string) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };
  
  // 导航栏样式
  const navbarClasses = `fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
    isScrolled
      ? 'bg-white/95 backdrop-blur-md shadow-lg'
      : 'bg-transparent'
  } ${className}`;
  
  // 移动端菜单动画
  const mobileMenuVariants = {
    hidden: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.3,
        ease: 'easeInOut' as const,
      },
    },
    visible: {
      opacity: 1,
      height: 'auto',
      transition: {
        duration: 0.3,
        ease: 'easeInOut' as const,
      },
    },
  };
  
  return (
    <nav className={navbarClasses}>
      <Container>
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <motion.div
            className="flex-shrink-0"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <button
              onClick={() => handleNavClick('#hero')}
              className="flex items-center"
            >
              <img 
                src="/logo.png" 
                alt="车为家" 
                className="h-24 w-24 object-contain"
              />
            </button>
          </motion.div>
          
          {/* 桌面端导航 */}
          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <div key={item.label} className="relative">
                {item.children ? (
                  <div className="relative">
                    <button
                      onClick={() => handleDropdownToggle(item.label)}
                      className={`flex items-center space-x-1 px-3 py-2 text-sm font-bold transition-colors duration-300 ${
                isScrolled ? 'text-gray-600' : lightText ? 'text-white' : 'text-gray-700'
              }`}
              style={{}}
              onMouseEnter={(e) => e.currentTarget.style.color = '#1677FF'}
              onMouseLeave={(e) => e.currentTarget.style.color = isScrolled ? '#4B5563' : lightText ? '#FFFFFF' : '#374151'}
                    >
                      <span>{item.label}</span>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    
                    <AnimatePresence>
                      {openDropdown === item.label && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2"
                        >
                          {item.children.map((child) => (
                            <button
                              key={child.label}
                              onClick={() => handleNavClick(child.href)}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-600 font-bold transition-colors duration-200"
                              style={{}}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#E6F0FF';
                                e.currentTarget.style.color = '#1677FF';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = '#4B5563';
                              }}
                            >
                              {child.label}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <button
                    onClick={() => handleNavClick(item.href)}
                    className={`flex items-center space-x-1 px-3 py-2 text-sm font-bold transition-colors duration-300 ${
                      activeId === item.href.replace('#', '')
                        ? ''
                        : isScrolled
                        ? 'text-gray-600'
                        : lightText ? 'text-white' : 'text-gray-700'
                    }`}
                    style={{
                      color: activeId === item.href.replace('#', '') ? '#1677FF' : undefined,
      
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#1677FF'}
                    onMouseLeave={(e) => {
                      if (activeId !== item.href.replace('#', '')) {
                        e.currentTarget.style.color = isScrolled ? '#4B5563' : lightText ? '#FFFFFF' : '#374151';
                      }
                    }}
                  >
                    <span>{item.label}</span>
                    {item.isCoreService && (
                      item.label === '车后服务' ? (
                        <Zap className="w-4 h-4 text-blue-500 fill-current" />
                      ) : item.label === '车务服务' ? (
                        <Car className="w-4 h-4 text-green-500 fill-current" />
                      ) : (
                        <Star className="w-4 h-4 text-yellow-500 fill-current" />
                      )
                    )}
                  </button>
                )}
              </div>
            ))}
            
            {/* CTA按钮 */}
            <button 
              onClick={() => setIsConsultationModalOpen(true)}
              style={{backgroundColor: '#1677FF'}}
              className="px-4 py-2 text-white font-bold rounded-lg hover:opacity-90 shadow-md hover:shadow-lg transition-all"
            >
              免费咨询
            </button>
          </div>
          
          {/* 移动端菜单按钮 */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`p-2 rounded-md transition-colors duration-300 ${
                isScrolled ? 'text-gray-700' : lightText ? 'text-white' : 'text-gray-700'
              }`}
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        
        {/* 移动端菜单 */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              variants={mobileMenuVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="lg:hidden overflow-hidden bg-white border-t border-gray-200"
            >
              <div className="py-4 space-y-2">
                {navItems.map((item) => (
                  <div key={item.label}>
                    {item.children ? (
                      <div>
                        <button
                          onClick={() => handleDropdownToggle(item.label)}
                          className="flex items-center justify-between w-full px-4 py-2 text-left text-gray-600 font-bold hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200"
                        >
                          <span>{item.label}</span>
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        
                        <AnimatePresence>
                          {openDropdown === item.label && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden bg-gray-50"
                            >
                              {item.children.map((child) => (
                                <button
                                  key={child.label}
                                  onClick={() => handleNavClick(child.href)}
                                  className="block w-full text-left px-8 py-2 text-sm text-gray-500 font-bold hover:text-blue-600 hover:bg-blue-50 transition-colors duration-200"
                                >
                                  {child.label}
                                </button>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleNavClick(item.href)}
                        className={`flex items-center justify-between w-full text-left px-4 py-2 font-bold transition-colors duration-200 hover:bg-blue-50 hover:text-blue-600 ${
                          activeId === item.href.replace('#', '')
                            ? 'text-blue-600 bg-blue-50'
                            : 'text-gray-600'
                        }`}
                      >
                        <span>{item.label}</span>
                        {item.isCoreService && (
                          item.label === '车后服务' ? (
                            <Zap className="w-4 h-4 text-blue-500 fill-current" />
                          ) : item.label === '车务服务' ? (
                            <Car className="w-4 h-4 text-green-500 fill-current" />
                          ) : (
                            <Star className="w-4 h-4 text-yellow-500 fill-current" />
                          )
                        )}
                      </button>
                    )}
                  </div>
                ))}
                
                <div className="px-4 pt-4">
                  <Button 
                    variant="primary" 
                    size="sm" 
                    className="w-full bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg"
                    onClick={() => setIsConsultationModalOpen(true)}
                  >
                    免费咨询
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Container>
      
      {/* 咨询弹窗 */}
      <ConsultationModal
        isOpen={isConsultationModalOpen}
        onClose={() => setIsConsultationModalOpen(false)}
      />
    </nav>
  );
};